import { useState } from "react";

import { useGetDatasetConnectors } from "@/api/dataset/useDatasetAPI";
import {
  InformationIcon,
  LokasiTargetinIcon,
  UploadIcon
} from "@/assets/icons";
import { Button } from "@/components/ui/button";
import {
  DialogContent,
  DialogDescription,
  DialogTitle
} from "@/components/ui/dialog";
import { ADD_DATASET_MESSAGE } from "@/constants/message";
import { cn } from "@/lib/utils";
import {
  AddDatasetSourceOption,
  DatasetConnectorItem,
  EAddDatasetSource,
  EDatasetConnector,
  EDatasetConnectorStatus
} from "@/types/dataset";

const TEST_ID_PREFIX = "add-dataset-dialog";

type SourceFlow = Pick<
  AddDatasetSourceOption,
  "key" | "requiresConfirmation" | "hint"
> & {
  /** Replaces the API's description when product wants its own copy. */
  description?: string;
};

/** What each connector does once picked. Codes missing here have no flow yet. */
const SOURCE_FLOWS: Partial<Record<string, SourceFlow>> = {
  [EDatasetConnector.LI]: {
    key: EAddDatasetSource.UPLOAD,
    // Opens the existing upload flow on selection, so the submit button stays disabled.
    requiresConfirmation: false
  },
  [EDatasetConnector.TARGETIN]: {
    key: EAddDatasetSource.TARGETIN,
    requiresConfirmation: true,
    description: ADD_DATASET_MESSAGE.TARGETIN_DESCRIPTION,
    hint: ADD_DATASET_MESSAGE.DURATION_HINT
  }
};

/** Local asset for each `icon_name` the API sends. */
const CONNECTOR_ICONS: Partial<Record<string, JSX.Element>> = {
  upload: <UploadIcon className="w-5 h-5 fill-teal-500" />,
  targetin: <LokasiTargetinIcon className="w-5 h-5" />
};

const toSourceOptions = (
  connectors: DatasetConnectorItem[]
): AddDatasetSourceOption[] =>
  connectors.flatMap((connector) => {
    const flow = SOURCE_FLOWS[connector.connector_code];

    if (connector.status !== EDatasetConnectorStatus.AVAILABLE || !flow) {
      return [];
    }

    return [
      {
        ...flow,
        title: connector.connector_name,
        description: flow.description ?? connector.description,
        icon: CONNECTOR_ICONS[connector.icon_name],
        isConnected: connector.is_active && connector.is_connected
      }
    ];
  });

export interface AddDatasetDialogProps {
  /** Dismisses the picker without choosing a source. */
  onCancel: () => void;
  /** Called with the chosen source when the user confirms. */
  onConfirm: (source: EAddDatasetSource) => void;
}

/** Source picker behind the "Add Dataset" CTA. */
const AddDatasetDialog = ({
  onCancel,
  onConfirm
}: Readonly<AddDatasetDialogProps>) => {
  const [selectedSource, setSelectedSource] =
    useState<EAddDatasetSource | null>(null);

  const { data: connectors = [], isLoading } = useGetDatasetConnectors();
  const sourceOptions = toSourceOptions(connectors);

  const selectedOption = sourceOptions.find(
    ({ key }) => key === selectedSource
  );

  /**
   * Targetin is only marked here and starts on submit. Upload hands over to the
   * existing upload flow as soon as it is picked, since the button is not its way in.
   */
  const handleSelect = (source: AddDatasetSourceOption) => {
    setSelectedSource(source.key);

    if (!source.requiresConfirmation) onConfirm(source.key);
  };

  return (
    <DialogContent
      className="flex flex-col gap-0 max-w-[520px]"
      data-testid={TEST_ID_PREFIX}
    >
      <div className="flex flex-col gap-3">
        <DialogTitle className="text-xl font-semibold leading-7 text-txtHeading">
          {ADD_DATASET_MESSAGE.TITLE}
        </DialogTitle>
        <DialogDescription className="text-sm font-normal text-txtBody">
          {ADD_DATASET_MESSAGE.DESCRIPTION}
        </DialogDescription>

        <div className="flex flex-col gap-3">
          {isLoading && (
            <div
              className="h-16 rounded-xl bg-gray-100 animate-pulse"
              data-testid={`${TEST_ID_PREFIX}-loading`}
            />
          )}

          {sourceOptions.map((source) => {
            // Already connected: nothing to import, so it shows as picked and stays locked.
            const isSelected =
              source.isConnected || source.key === selectedSource;

            return (
              <button
                key={source.key}
                type="button"
                aria-pressed={isSelected}
                disabled={source.isConnected}
                data-testid={`${TEST_ID_PREFIX}-source-${source.key}`}
                onClick={() => handleSelect(source)}
                className={cn(
                  "flex  gap-3 p-3 text-left border rounded-xl transition-colors items-center",
                  isSelected
                    ? "border-teal-500"
                    : "border-neutral-200 hover:border-teal-200"
                )}
              >
                <span className="flex items-center justify-center w-10 h-10 rounded-lg shrink-0 bg-teal-50">
                  {source.icon}
                </span>
                <span className="flex flex-col gap-0.5">
                  <span className="text-sm font-semibold text-txtHeading">
                    {source.title}
                  </span>
                  <span className="text-xs text-txtBody">
                    {source.description}
                  </span>
                </span>
                {source.isConnected && (
                  <span
                    className="px-3 py-1 ml-auto text-xs font-medium border rounded-lg shrink-0 border-teal-500 bg-teal-50 text-teal-500"
                    data-testid={`${TEST_ID_PREFIX}-connected-${source.key}`}
                  >
                    {ADD_DATASET_MESSAGE.CONNECTED}
                  </span>
                )}
              </button>
            );
          })}

          {selectedOption?.hint && (
            <div className="flex items-center gap-2 p-3 border rounded-xl border-royal-blue/30 bg-royal-blue/5">
              <InformationIcon className="w-4 h-4 shrink-0 fill-royal-blue" />
              <p className="text-xs text-txtHeading">{selectedOption.hint}</p>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-row w-full gap-3 mt-6">
        <Button
          variant="outline"
          className="py-2.5 h-auto w-full rounded-xl font-semibold text-sm"
          data-testid={`${TEST_ID_PREFIX}-cancel`}
          onClick={onCancel}
        >
          {ADD_DATASET_MESSAGE.CANCEL}
        </Button>
        <Button
          variant="default"
          className="py-2.5 h-auto w-full rounded-xl font-semibold text-sm"
          data-testid={`${TEST_ID_PREFIX}-confirm`}
          // Only sources that submit through this button can enable it.
          disabled={!selectedOption?.requiresConfirmation}
          onClick={() => selectedSource && onConfirm(selectedSource)}
        >
          {ADD_DATASET_MESSAGE.SUBMIT}
        </Button>
      </div>
    </DialogContent>
  );
};

export default AddDatasetDialog;
